import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { INVITATION_EXPIRY_MS } from '../../utils/postAuthRoute';
import { consumeForShopifyReturn } from '../../utils/shopifyGrantFlowProject';
import { buildProjectPath } from '../../utils/projectPath';

/**
 * Renders at the bare (unscoped) /grant-access route. Two live reasons this
 * exists, not one:
 *
 *   1. It is the permanent landing target of cq-api's Shopify OAuth
 *      callback's cookie-missing failure branch (shopify.router.js redirects
 *      to `${FRONTEND_URL}/grant-access?provider=shopify&status=failed`
 *      whenever the sfid/nonce cookies fail to round-trip during OAuth) --
 *      an ongoing failure mode, unrelated to bookmarks.
 *   2. It catches any stale bare /grant-access bookmark/link from before
 *      project-scoped routing existed (the sibling /analyze/* and
 *      /settings bare-path catchers this component used to also serve were
 *      confirmed bookmark-only dead weight, pre-launch with zero real
 *      users, and have been removed).
 *
 * First tries to recover the project that initiated the OAuth flow via
 * consumeForShopifyReturn() (sessionStorage); if that fails (or there was no
 * in-flight Shopify return), falls back to the same generic
 * project-resolution precedence used elsewhere in the app (first
 * project, else a non-expired pending invitation, else create-project).
 * Resolves the correct project and redirects (replace) to the scoped
 * /grant-access equivalent, preserving the query string. Renders null while
 * resolving.
 *
 * Props:
 *   projects         - user.projects (array)
 *   pendingInvitations  - user.pending_invitations (array)
 */
const LegacyProjectRedirect = ({ projects, pendingInvitations }) => {
  const location = useLocation();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const project = consumeForShopifyReturn();
    if (project) {
      setTarget(buildProjectPath(project.id, `/grant-access${location.search}`));
      return;
    }

    const list = projects || [];
    if (list.length === 0) {
      const now = Date.now();
      const hasNonExpiredInvitation = (pendingInvitations || []).some(
        (inv) => now - Number(inv.created_at) < INVITATION_EXPIRY_MS
      );
      setTarget(hasNonExpiredInvitation ? '/accept-invite' : '/create-project');
      return;
    }

    const projectId = list[0].id;
    setTarget(buildProjectPath(projectId, `/grant-access${location.search}`));
    // Resolve once per mount (i.e. once per navigation to this legacy
    // path) -- projects/pendingInvitations are already a fresh fetch by
    // the time any route renders (App.js's profileLoading gate), so there
    // is nothing to react to on a later prop change here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!target) return null;
  return <Navigate to={target} replace />;
};

export default LegacyProjectRedirect;
